import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Download, ExternalLink, X } from "lucide-react";

export const GuardiePicker = ({ guardie, value, onChange, lockedId }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? guardie.filter((g) => `${g.cognome} ${g.nome}`.toLowerCase().includes(s)) : guardie;
  }, [guardie, q]);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const byId = Object.fromEntries(guardie.map((g) => [g.id, g]));
  return (
    <div className="space-y-2" data-testid="verbale-guardie-picker">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-[#166534]" data-testid={`verbale-guardia-chip-${id}`}>
              {byId[id] ? `${byId[id].cognome} ${byId[id].nome}` : "Guardia"}
              {id !== lockedId && <button type="button" onClick={() => toggle(id)} aria-label="Rimuovi"><X className="h-3 w-3" /></button>}
            </span>
          ))}
        </div>
      )}
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca guardia per cognome o nome" data-testid="verbale-guardie-search" />
      <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 p-1 gz-scroll">
        {shown.map((g) => (
          <label key={g.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
            <Checkbox checked={value.includes(g.id)} disabled={g.id === lockedId} onCheckedChange={() => toggle(g.id)} data-testid={`verbale-guardia-option-${g.id}`} />
            {g.cognome} {g.nome}{g.id === lockedId ? " (tu)" : ""}
          </label>
        ))}
        {shown.length === 0 && <p className="px-2 py-2 text-xs text-slate-400">Nessuna guardia trovata.</p>}
      </div>
    </div>
  );
};

const toBlobUrl = (dataUrl) => {
  const [head, b64] = dataUrl.split(",");
  const mime = (head.match(/data:([^;]+)/) || [])[1] || "application/octet-stream";
  const bin = atob(b64 || "");
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) { arr[i] = bin.charCodeAt(i); }
  return URL.createObjectURL(new Blob([arr], { type: mime }));
};

const openAllegato = (a) => { window.open(toBlobUrl(a.data), "_blank", "noopener"); };
const downloadAllegato = (a) => {
  const link = document.createElement("a");
  link.href = toBlobUrl(a.data);
  link.download = a.name || "allegato";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const AllegatiDialog = ({ intervento, onClose }) => (
  <Dialog open={Boolean(intervento)} onOpenChange={(o) => { if (!o) { onClose(); } }}>
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl gz-scroll" data-testid="allegati-dialog">
      <DialogHeader><DialogTitle>Allegati {intervento?.protocollo}</DialogTitle></DialogHeader>
      <div className="grid gap-4 sm:grid-cols-2">
        {(intervento?.allegati || []).map((a, i) => {
          const isImg = (a.type || "").startsWith("image/") || /^data:image\//.test(a.data || "");
          return (
            <div key={i} className="overflow-hidden rounded-lg border border-slate-200" data-testid={`allegato-${i}`}>
              {isImg ? <img src={a.data} alt={a.name} className="max-h-72 w-full bg-slate-50 object-contain" />
                : <div className="flex h-40 items-center justify-center bg-slate-50"><FileText className="h-12 w-12 text-red-700" /></div>}
              <div className="flex items-center justify-between gap-2 p-2">
                <p className="min-w-0 truncate text-xs font-medium text-slate-700" title={a.name}>{a.name}</p>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openAllegato(a)} data-testid={`allegato-open-${i}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => downloadAllegato(a)} data-testid={`allegato-download-${i}`}><Download className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DialogContent>
  </Dialog>
);
